import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from '../../../utils/lodash';
import { refSetter } from '../../../utils/helpers';
import TimelineRow from '../TimelineRow/TimelineRow';
import { hourWidth } from '../../../settings/scheduler';
import globals from '../../../globals/communityGlobals';
import gradePropTypes from '../../../propTypes/entities/grade';
import { parseDateUtc } from '../../../utils/dates';
import { cardTypes } from '../../../settings/constants/cardTypes';
import { AssignmentVisitSchedule, AssignmentVisitState, AssignmentType } from '../../../enums';

const locale = globals.getArea('dailyPlanning');
class Timeline extends React.Component {
  constructor(props) {
    super(props);
    this.state = { scrollNumber: 0 };

    this.isComponentMount = true;
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScrollThrottled);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.state.scrollNumber !== nextState.scrollNumber ||
      this.props.now !== nextProps.now ||
      this.props.dayStart !== nextProps.dayStart ||
      this.props.dayEnd !== nextProps.dayEnd ||
      this.props.postings.length !== nextProps.postings.length ||
      this.getJoinedPostingIds(this.props) !== this.getJoinedPostingIds(nextProps) ||
      this.getJoinedAvailabilitiesAssignGuids(this.props) !== this.getJoinedAvailabilitiesAssignGuids(nextProps)
    );
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScrollThrottled);
  }

  /* eslint-disable class-methods-use-this */
  getJoinedAvailabilitiesAssignGuids(target) {
    return target.postings.map(p => p.availabilities.map(a => a.assignGuid).join(',')).join(',');
  }

  getJoinedPostingIds(target) {
    return target.postings.map(p => p.id).join(',');
  }
  /* eslint-enable class-methods-use-this */

  setRef = refSetter.bind(this);

  getDayWidth = () => this.getDayHours() * hourWidth;
  getDayHours = () =>
    Math.ceil(moment.duration(this.parsedDayEnd.diff(this.parsedDayStart.startOf('hour'))).asMinutes() / 60);
  getHoursFromDayStart = () =>
    moment.duration(this.parsedNow.diff(this.parsedDayStart.startOf('hour'))).asMinutes() / 60;

  getHeaderStyle = dayWidth => ({ width: `${dayWidth}px`, transform: `translateY(${this.state.scrollNumber}px)` });

  parseHours = date => parseInt(this.parseDate(date).hours(), 10);
  parseDate = date => moment(date).utcOffset(0, true);

  get parsedDayEnd() {
    return this.parseDate(this.props.dayEnd).clone();
  }
  get parsedDayStart() {
    return this.parseDate(this.props.dayStart).clone();
  }
  get parsedNow() {
    return this.parseDate(this.props.now).clone();
  }

  isToday = () => this.parsedNow.isBefore(this.parsedDayEnd) && this.parsedNow.isAfter(this.parsedDayStart);

  generateTimelineHeader = () => {
    const dayHours = this.getDayHours();
    const headerCellStyle = { width: `${hourWidth}px` };
    const timelineHeader = [];

    for (let i = 0; i < dayHours; i += 1) {
      timelineHeader.push(
        <div key={`schedulerTimelineHeader${i}`} className="header-cell" style={headerCellStyle}>
          {this.parsedDayStart.add(i, 'hours').format(globals.timeFormats.timelineHeader)}
        </div>,
      );
    }

    return timelineHeader;
  };

  generateTimelineRows = dayWidth =>
    this.props.postings.map(el => {
      const dayStart = this.parsedDayStart.startOf('hour').format(globals.dateFormats.localApiFormat);
      const dayEnd = this.parsedDayEnd.startOf('hour').format(globals.dateFormats.localApiFormat);
      return (
        <TimelineRow
          key={`timelineRow${el.id}`}
          postingId={el.id}
          clinicianGrade={el.grade}
          dayWidth={dayWidth}
          dayStart={dayStart}
          dayEnd={dayEnd}
          availabilities={el.availabilities}
          canAllocateWork={this.props.canAllocateWork}
          clinicianId={el.clinician.id}
        />
      );
    });

  generateCurrTimeStyle = (height, transform) => ({ height, transform });

  generateCurrentTime = () => {
    const currentTime = this.getHoursFromDayStart() * hourWidth;
    const { scrollNumber } = this.state;
    const headerHeight = 37;
    const height = `calc(100% - ${scrollNumber + headerHeight}px)`;
    const transform = `translate(${currentTime + 150}px, ${scrollNumber + headerHeight}px)`;
    const currentTimeStyle = this.generateCurrTimeStyle(height, transform);
    return (
      <div className="current-time" style={currentTimeStyle}>
        <span>{locale.getMsg('current')}</span>
      </div>
    );
  };

  handleScroll = () => {
    const scrollTops = this.timelineWrapper.getBoundingClientRect().top;
    let nextScrollTopPosition;
    if (scrollTops <= 0) {
      nextScrollTopPosition = Math.abs(scrollTops) - 1;
    } else {
      nextScrollTopPosition = 0;
    }
    this.setState({ scrollNumber: nextScrollTopPosition });
  };
  handleScrollThrottled = _.throttle(this.handleScroll, 10, { trailing: true });

  isValidSlicedItemBefore = (start, end, startRangeDate, endRangeDate) =>
    parseDateUtc(start).diff(startRangeDate, 'minutes') < 0 &&
    parseDateUtc(end).isBetween(startRangeDate, endRangeDate);

  isValidSlicedItemAfter = (start, end, startRangeDate, endRangeDate) =>
    parseDateUtc(start).isBetween(startRangeDate, endRangeDate) && parseDateUtc(end).diff(endRangeDate, 'minutes') > 0;

  generateTimelineSlicedAssignments = (postings, dayStart, dayEnd, isValidSlicedItemHandler, isLeft) =>
    postings.map(p => {
      const schedulerModeStartTimeObject = parseDateUtc(dayStart);
      const schedulerModeEndTimeObject = parseDateUtc(dayEnd);

      const slicedItemRender = () => {
        let assignments = [];
        _.each(p.availabilities, av => {
          const postingAssignments = _.filter(av.assignments, assignment => {
            const { startDate, endDate, visitStatus, visualStartDate, visualEndDate } = assignment;
            const isEarlyCompleted =
              visitStatus === AssignmentVisitState.Complete || visitStatus === AssignmentVisitState.Complete;

            const startTime = isEarlyCompleted ? visualStartDate : startDate;
            const endTime = isEarlyCompleted ? visualEndDate : endDate;

            return isValidSlicedItemHandler(
              startTime,
              endTime,
              schedulerModeStartTimeObject,
              schedulerModeEndTimeObject,
            );
          });

          if (postingAssignments != null && postingAssignments.length > 0) {
            assignments = _.concat(postingAssignments, assignments);
          }
        });

        if (assignments != null && assignments.length > 0) {
          let assignmentTypeClass = 'planned';
          const slicedAssignment = assignments[0];
          const { visitSchedule, cardType, visitStatus, assignmentType } = slicedAssignment;

          if (assignmentType === AssignmentType.LunchBreak) return null;

          const visitStart = parseDateUtc(slicedAssignment.visitStarted || slicedAssignment.visitStartDate);
          const visitEnd = parseDateUtc(slicedAssignment.visualEndDate);

          switch (visitStatus) {
            case AssignmentVisitState.Planned:
              if (cardType === cardTypes.ADDITIONAL_CLINICIAN || cardType === cardTypes.OVERRIDDEN_CLINICIAN) {
                assignmentTypeClass += '-copy';
              }
              if (visitSchedule === AssignmentVisitSchedule.SpecificTime) {
                assignmentTypeClass += '-fixed';
              } else if (visitSchedule === AssignmentVisitSchedule.SingleTimeSlot) {
                assignmentTypeClass += '-timeslot';
              }
              break;
            case AssignmentVisitState.Behind:
              assignmentTypeClass = 'behind';
              break;
            case AssignmentVisitState.Ahead:
              assignmentTypeClass = 'ahead';
              break;
            case AssignmentVisitState.Complete:
              assignmentTypeClass = 'complete';
              break;
            default:
              assignmentTypeClass = 'planned';
          }

          const itemLeftStyle = isLeft
            ? `assignment-info-wrapper asw-event-card--${assignmentTypeClass}-offset left`
            : `assignment-info-wrapper asw-event-card--${assignmentTypeClass}-offset`;

          return (
            <div className="planned content">
              <div className={itemLeftStyle}>
                <div className="assignment-info assignment-master">
                  {visitStatus !== AssignmentVisitState.Complete && (
                    <div className="icon-container">
                      <span className="icon-travel icon-svg asw-icon" role="button" />
                      <span className="assignment-time">
                        {parseDateUtc(slicedAssignment.startDate).format(globals.timeFormats.short)} -{' '}
                        {visitStart.format(globals.timeFormats.short)}
                      </span>
                    </div>
                  )}
                  <div className="icon-container">
                    <span className="icon-person-type-amber icon-svg asw-icon" />
                    <span className="assignment-time">
                      {visitStart.format(globals.timeFormats.short)} - {visitEnd.format(globals.timeFormats.short)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        return null;
      };

      return (
        <li id={`timelinePostingId${p.id}`} key={`timelinePostingId${p.id}`} className="clinician-row">
          {slicedItemRender()}
        </li>
      );
    });

  calculateBeforeAssignments = () => {
    const { isNightMode, dayStart, dayEnd, postings } = this.props;

    const beforeAssignments = this.generateTimelineSlicedAssignments(
      postings,
      dayStart,
      dayEnd,
      this.isValidSlicedItemBefore,
      true,
    );
    const headerText = isNightMode
      ? parseDateUtc(dayStart)
          .format('Do MMM')
          .concat(' Day')
      : parseDateUtc(dayStart)
          .hour(-1)
          .format('Do MMM')
          .concat(' Night');

    return { headerText, beforeAssignments };
  };

  calculateAfterAssignments = () => {
    const { isNightMode, dayStart, dayEnd, postings } = this.props;

    const afterAssignments = this.generateTimelineSlicedAssignments(
      postings,
      dayStart,
      dayEnd,
      this.isValidSlicedItemAfter,
    );
    const headerAfterText = isNightMode
      ? parseDateUtc(dayEnd)
          .format('Do MMM')
          .concat(' Day')
      : parseDateUtc(dayStart)
          .format('Do MMM')
          .concat(' Night');

    return { headerAfterText, afterAssignments };
  };

  render() {
    const { postings, isFetching, isAutoAllocating } = this.props;
    const header = this.generateTimelineHeader();
    const dayWidth = this.getDayWidth();
    const timelineRows = this.generateTimelineRows(dayWidth);
    const { headerText, beforeAssignments } = this.calculateBeforeAssignments();
    const { headerAfterText, afterAssignments } = this.calculateAfterAssignments();
    const hasNoData = !postings || postings.length < 1;

    if (hasNoData || isFetching || isAutoAllocating) {
      let msg = locale.getMsg('noCliniciansAvailable');
      if (this.isComponentMount || isFetching) {
        msg = 'Fetching data...';
      } else if (isAutoAllocating) {
        msg = 'Auto Allocation is in progress...';
      }

      if (this.isComponentMount) {
        this.isComponentMount = false;
      }
      return <div style={{ padding: '10px 0', fontSize: '1.6rem' }}>{msg}</div>;
    }

    return (
      <div ref={this.setRef('timelineWrapper')} className="timeline">
        {this.isToday() && this.generateCurrentTime()}
        <div
          className="timeline-previousDay"
          style={{ width: '150px', display: 'inline-block', height: '100%', verticalAlign: 'top' }}
        >
          <div className="header">
            <div className="header-cell" style={{ width: '150px', backgroundColor: '#F3F3F3' }}>
              {headerText}
            </div>
          </div>
          <ul>{beforeAssignments}</ul>
        </div>
        <div
          className="timeline-currentDay"
          style={{ display: 'inline-block', position: 'relative', boxShadow: '2px -1px 24px -11px rgba(0, 0, 0, 1)' }}
        >
          <div className="header" style={this.getHeaderStyle(dayWidth)}>
            {header}
          </div>
          <ul>{timelineRows}</ul>
        </div>
        <div
          className="timeline-nextDay"
          style={{ width: '150px', display: 'inline-block', height: '100%', verticalAlign: 'top' }}
        >
          <div className="header">
            <div className="header-cell" style={{ width: '150px', backgroundColor: '#F3F3F3' }}>
              {headerAfterText}
            </div>
          </div>
          <ul>{afterAssignments}</ul>
        </div>
      </div>
    );
  }
}

Timeline.propTypes = {
  postings: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      grade: gradePropTypes.isRequired,
      clinician: PropTypes.shape({
        clinician: PropTypes.string,
        surname: PropTypes.string,
      }),
      availabilities: PropTypes.arrayOf(
        PropTypes.shape({
          start: PropTypes.start,
          end: PropTypes.string,
          id: PropTypes.number,
        }),
      ),
    }),
  ).isRequired,
  canAllocateWork: PropTypes.bool.isRequired,
  dayStart: PropTypes.string.isRequired,
  dayEnd: PropTypes.string.isRequired,
  now: PropTypes.string.isRequired,
  isNightMode: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  isAutoAllocating: PropTypes.bool.isRequired,
};
export default Timeline;
